// A2-637 Owner B (operator's Step 4 check 2026-11-02): a card's "What to
// do" is built from the answers it actually draws. The server sends one
// token per answer (g.actions, api/_review_answers/actions.py); this file
// turns each into a sentence in the reader's language (en.json / cn.json
// ui.card_actions). It names no flag, so no card can promise a button it
// does not have ("for all 6356 rows", "Leave out the copy", "mark
// reviewed" on a note).

const GOTO_KEY = (tok) => [tok.target, tok.scheme].filter(Boolean).join(".").replace(/\./g, "_").toLowerCase();

const DEFAULTS = {
  PICK_RATE: ["Pick the VAT rate for all {{n}} rows, or for the rows you tick.", "Tick the rows and pick their VAT rate ({{n}} rows)."],
  CREDIT_NOTE_CHANNEL: ["Say what kind of sale these credit notes reverse: once for all {{n}} rows, or for the rows you tick.", "On each credit note, say what kind of sale it reverses ({{n}} rows)."],
  LEAVE_OUT_COPY: ["Leave out all {{n}} copies (the first row stays), or keep a copy that is a real sale.", "Leave out the rows that are copies ({{n}} rows); the first row stays."],
  CUSTOMER_COUNTRY: ["Say where all {{n}} customers belong, or answer row by row.", "Say where each customer belongs ({{n}} rows)."],
  BUSINESS_EVIDENCE: ["If you hold proof the customers are businesses, record it for all {{n}}, or row by row.", "If you hold proof a customer is a business, record it on the row ({{n}} rows)."],
  CLEAR_DECISION: ["Clear the earlier answer on all {{n}} rows, then answer again.", "Clear the earlier answer on the row, then answer again ({{n}} rows)."],
  PROPERTY_LETTING: ["Say whether it is rent for the property itself, for all {{n}} rows or row by row.", "Say whether each row is rent for the property itself ({{n}} rows)."],
  VAT_NUMBER: [null, "Add or correct the buyer's VAT number on the row ({{n}} rows)."],
  PICK_COUNTRY: [null, "Pick the country on each row ({{n}} rows)."],
  SERVICE_CATEGORY: [null, "Pick the kind of service on each row ({{n}} rows)."],
  ESTABLISHMENT: [null, "Say where each customer is established ({{n}} rows)."],
  SOLD_AS: [null, "Say what was sold on each row: a digital service or an event ticket ({{n}} rows)."],
  FX_TREATMENT: [null, "Correct how the exchange rate was applied on the row ({{n}} rows)."],
  ONLINE_EVENT_CHOICE: ["Choose which country's VAT applies to this client's online-event tickets.", "Choose which country's VAT applies to this client's online-event tickets."],
  ADD_REGISTRATION: ["Add the registration to the client record.", "Add the registration to the client record."],
  FIX_IN_FILE: ["Correct {{columns}} in the file, upload it again and re-run.", "Correct {{columns}} in the file for those rows, upload it again and re-run."],
  NO_WAY_OUT: ["The app cannot record this answer yet: ask for help before filing.", "The app cannot record this answer yet: ask for help before filing."],
  KEEP: ["Or, if it is right, leave all {{n}} as they are.", "Or keep a row as it is, with a reason ({{n}} rows)."],
};

/** One sentence per token, in order. */
export const actionSentences = (g, t) => (g.actions || []).map((tok) => {
  const n = tok.rows;
  const scope = tok.all ? "all" : "rows";
  if (tok.kind === "GO_TO") {
    const key = `ui.review_answers.goto.${GOTO_KEY(tok)}`;
    const found = t(key, { defaultValue: "" });
    // a link with no words of its own never shows its internal target
    if (!found || found === key) return t("ui.card_actions.goto_other", { defaultValue: "Use the link on this card, then re-run." });
    const place = found.replace(/\s*↗\s*$/, "");
    return t("ui.card_actions.GO_TO", { place, defaultValue: "{{place}}, then re-run." });
  }
  const [allText, rowsText] = DEFAULTS[tok.kind] || [null, null];
  const fallback = (tok.all ? allText : rowsText) || allText || rowsText;
  if (!fallback) return null;
  return t(`ui.card_actions.${tok.kind}.${scope}`, {
    n, columns: (tok.columns || []).join(", "), defaultValue: fallback,
  });
}).filter(Boolean);

/** The card's "What to do", or the note's "nothing to do". A card to
 * answer that draws no answer is a dead end the guard over every card
 * (A2-637 step 7) refuses; until then it keeps the old general line. */
export const cardInstruction = (g, t) => {
  if (g.calc_level) {
    return t("ui.card_actions.calc_level", { defaultValue: "Use the step shown on this card: it applies to the whole calculation." });
  }
  const s = actionSentences(g, t);
  if (s.length) return s.join(" ");
  if (g.tier === "info" || (!g.tier && g.audit_only)) {
    return t("ui.card_actions.none", {
      defaultValue: "Nothing to do: this explains what the app did with these rows.",
    });
  }
  return t("ui.review_grouped.action_default", { defaultValue: "Review each row and record a decision." });
};

export default cardInstruction;
